import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import { EditorContent, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import { getMarkRange } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { DELETION, INSERTION, type TranscriptDoc } from '../utils/transcriptDoc';
import { Deletion, Insertion, placeCaret, trackDelete } from '../utils/transcriptMarks';
import type { TranscriptComment } from '../utils/transcriptComments';
import { formatEditStamp } from '../utils/time';
interface TranscriptEditorProps {
  doc: TranscriptDoc;
  editable: boolean;
  /** Bumped when the document must be rebuilt from `doc` (load, save, restore). */
  resetKey: number;
  onChange: (doc: TranscriptDoc) => void;
  /** Comments whose passage is still present, drawn in the margin. */
  comments: TranscriptComment[];
  canComment: boolean;
  onComment: (input: {
    quote: string;
    quoteStart: number;
    body: string;
  }) => Promise<boolean>;
}
const EDITOR_CLASS = 'min-h-[38rem] w-full whitespace-pre-wrap border border-rule bg-paperDeep px-6 py-6 font-display text-xl leading-relaxed text-charcoal focus:outline-none sm:px-9 sm:py-8 sm:text-2xl';

/** The readable text of each paragraph — struck wording excluded. */
function paragraphTexts(editor: Editor): string[] {
  const out: string[] = [];
  editor.state.doc.forEach(node => {
    let text = '';
    node.forEach(child => {
      if (!child.isText) return;
      if (child.marks.some(mark => mark.type.name === DELETION)) return;
      text += child.text ?? '';
    });
    out.push(text);
  });
  return out;
}

/** Which paragraph a comment belongs beside, or -1 when it cannot be placed. */
function paragraphFor(paragraphs: string[], comment: TranscriptComment): number {
  if (!comment.quote) return -1;
  let offset = 0;
  let fallback = -1;
  for (let index = 0; index < paragraphs.length; index += 1) {
    const at = paragraphs[index].indexOf(comment.quote);
    if (at !== -1) {
      if (fallback === -1) fallback = index;
      if (comment.quoteStart >= 0 && Math.abs(offset + at - comment.quoteStart) < 60) return index;
    }
    offset += paragraphs[index].length + 1;
  }
  return fallback;
}
interface Marker {
  paragraph: number;
  top: number;
  comments: TranscriptComment[];
}
interface Bubble {
  top: number;
  left: number;
  quote: string;
  quoteStart: number;
  /** The selection is entirely struck wording, so the action is Restore. */
  struck: boolean;
}

/**
 * The transcript, as a structured document.
 *
 * Deleting never removes Portal's words: the selection is marked deleted and
 * stays exactly where it is, faded and struck through, from the moment the key
 * is pressed. Wording the reader added themselves deletes normally, because it
 * was never part of the delivered transcript.
 *
 * Comments live with the text: select a passage for the contextual actions, and
 * saved comments show as a quiet marker in the right margin beside their own
 * paragraph, which stays with that paragraph as the page scrolls.
 */
export function TranscriptEditor({
  doc,
  editable,
  resetKey,
  onChange,
  comments,
  canComment,
  onComment
}: TranscriptEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bubble, setBubble] = useState<Bubble | null>(null);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState('');
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [openMarker, setOpenMarker] = useState<number | null>(null);
  /** Bumped whenever the rendered document may have moved. */
  const [measure, setMeasure] = useState(0);

  /** The contextual actions, positioned against the current selection. */
  const readSelection = useCallback((instance: Editor) => {
    const container = containerRef.current;
    if (!container) return;
    const {
      from,
      to,
      empty
    } = instance.state.selection;
    if (empty || !instance.isFocused) {
      setBubble(null);
      setComposing(false);
      return;
    }
    const quote = instance.state.doc.textBetween(from, to, ' ').trim();
    if (!quote) {
      setBubble(null);
      return;
    }

    /* Where the passage sits in the readable text, so the comment can be
       found again after the transcript changes. */
    const readable = paragraphTexts(instance);
    let offset = 0;
    let quoteStart = -1;
    for (const paragraph of readable) {
      const at = paragraph.indexOf(quote);
      if (at !== -1) {
        quoteStart = offset + at;
        break;
      }
      offset += paragraph.length + 1;
    }
    let struck = true;
    instance.state.doc.nodesBetween(from, to, node => {
      if (!node.isText) return;
      if (!node.marks.some(mark => mark.type.name === DELETION)) struck = false;
    });
    const box = container.getBoundingClientRect();
    const coords = instance.view.coordsAtPos(from);
    setBubble({
      top: coords.top - box.top - 44,
      left: Math.max(0, coords.left - box.left),
      quote,
      quoteStart,
      struck
    });
    setComposing(false);
  }, []);
  const editor = useEditor({
    extensions: [Document, Paragraph.configure({
      HTMLAttributes: {
        class: 'mb-5'
      }
    }), Text, History, Deletion, Insertion],
    content: doc,
    editable,
    editorProps: {
      attributes: {
        class: EDITOR_CLASS,
        'aria-label': 'Transcript'
      },
      /* Backspace and Delete never remove Portal's words. */
      handleKeyDown(view, event) {
        if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
        if (!view.editable) return false;
        const {
          state
        } = view;
        const {
          empty,
          from,
          to
        } = state.selection;
        const backspace = event.key === 'Backspace';
        const start = empty ? backspace ? from - 1 : from : from;
        const end = empty ? backspace ? from : from + 1 : to;
        if (start < 0 || end > state.doc.content.size || start >= end) return true;
        const tr = state.tr;
        if (trackDelete(state, tr, start, end)) {
          placeCaret(tr, start);
          view.dispatch(tr);
        }
        /* Handled either way: nothing is ever removed by a keypress. */
        return true;
      },
      /* Typed wording is marked as an addition. */
      handleTextInput(view, from, to, text) {
        if (!view.editable) return false;
        const state = view.state;
        const insertionType = state.schema.marks[INSERTION];
        const deletionType = state.schema.marks[DELETION];
        if (!insertionType) return false;
        const tr = state.tr;
        let at = from;

        /* Typing over a selection strikes it first, then adds alongside. */
        if (from !== to) {
          trackDelete(state, tr, from, to);
          at = tr.mapping.map(to);
        }
        tr.insertText(text, at, at);
        if (deletionType) tr.removeMark(at, at + text.length, deletionType);
        tr.addMark(at, at + text.length, insertionType.create());
        placeCaret(tr, at + text.length);
        view.dispatch(tr);
        return true;
      },
      /* Clicking struck wording puts it back. */
      handleClick(view, pos, event) {
        const target = event.target as HTMLElement | null;
        if (!target?.closest?.('[data-deletion]')) return false;
        if (!view.editable) return false;
        const deletionType = view.state.schema.marks[DELETION];
        if (!deletionType) return false;
        const range = getMarkRange(view.state.doc.resolve(pos), deletionType);
        if (!range) return false;
        const tr = view.state.tr.removeMark(range.from, range.to, deletionType);
        view.dispatch(tr);
        return true;
      }
    },
    onUpdate: ({
      editor: instance
    }) => {
      onChange(instance.getJSON() as TranscriptDoc);
      setMeasure(value => value + 1);
    },
    onSelectionUpdate: ({
      editor: instance
    }) => {
      readSelection(instance);
    }
  }, [resetKey]);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  /* Markers are measured from the rendered document, so each sits beside its
     own paragraph and stays there as the page scrolls. */
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!editor || !container) return;
    const paragraphs = paragraphTexts(editor);
    const positions: number[] = [];
    editor.state.doc.forEach((_node, offset) => positions.push(offset + 1));
    const grouped = new Map<number, TranscriptComment[]>();
    comments.forEach(comment => {
      const index = paragraphFor(paragraphs, comment);
      if (index < 0 || index >= positions.length) return;
      const list = grouped.get(index) ?? [];
      list.push(comment);
      grouped.set(index, list);
    });
    const box = container.getBoundingClientRect();
    const next: Marker[] = [];
    grouped.forEach((list, index) => {
      try {
        const coords = editor.view.coordsAtPos(positions[index]);
        next.push({
          paragraph: index,
          top: coords.top - box.top,
          comments: list
        });
      } catch {
        /* A position that cannot be measured simply has no marker. */
      }
    });
    setMarkers(next);
  }, [editor, comments, measure, resetKey]);
  useEffect(() => {
    const onResize = () => setMeasure(value => value + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  async function submit() {
    if (!bubble || !body.trim() || sending) return;
    setSending(true);
    setFailed('');
    const ok = await onComment({
      quote: bubble.quote,
      quoteStart: bubble.quoteStart,
      body: body.trim()
    });
    setSending(false);
    if (!ok) {
      setFailed('Not saved — try again.');
      return;
    }
    setBody('');
    setComposing(false);
    setBubble(null);
  }
  function deleteSelection() {
    if (!editor) return;
    const {
      from,
      to
    } = editor.state.selection;
    const tr = editor.state.tr;
    if (trackDelete(editor.state, tr, from, to)) {
      placeCaret(tr, from);
      editor.view.dispatch(tr);
    }
    setBubble(null);
  }
  function restoreSelection() {
    if (!editor) return;
    const deletionType = editor.state.schema.marks[DELETION];
    if (!deletionType) return;
    const {
      from,
      to
    } = editor.state.selection;
    editor.view.dispatch(editor.state.tr.removeMark(from, to, deletionType));
    setBubble(null);
  }
  const open = markers.find(marker => marker.paragraph === openMarker);
  return <div ref={containerRef} className="relative pr-10" data-id="element-445" data-mp="25ezh11fwi2ev">
      <EditorContent editor={editor} data-id="element-446" data-mp="25efypjmc9c0b" />

      {/* Contextual actions, beside the selection. */}
      {bubble && editable && !composing && <div style={{
      top: bubble.top,
      left: bubble.left
    }} className="absolute z-20 flex items-center gap-5 border border-ink bg-paper px-4 py-2 shadow-sm" data-id="element-447" data-mp="fxu4xe2q57fc">
          {canComment && <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => setComposing(true)} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-448" data-mp="pvi2scvmmv1q">
              Comment
            </button>}
          {bubble.struck ? <button type="button" onMouseDown={event => event.preventDefault()} onClick={restoreSelection} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-449" data-mp="24cnpzxig6sh">
              Restore
            </button> : <button type="button" onMouseDown={event => event.preventDefault()} onClick={deleteSelection} className="label text-graphite transition-colors duration-150 ease-out hover:text-ink" data-id="element-450" data-mp="3enu92pp39zr3">
              Delete
            </button>}
        </div>}

      {/* The comment box, beside the passage it is about. */}
      {bubble && composing && <div style={{
      top: bubble.top,
      left: bubble.left
    }} className="absolute z-30 w-[22rem] max-w-[90vw] border border-ink bg-paper p-5 shadow-sm" data-id="element-451" data-mp="ql55zm4t16f9">
          <p className="m-0 border-l-2 border-rule pl-3 font-display text-base leading-snug text-graphite" data-id="element-452" data-mp="3u1c5rhk3mjir">
            “{bubble.quote.length > 120 ? `${bubble.quote.slice(0, 120)}…` : bubble.quote}”
          </p>
          <textarea value={body} onChange={event => setBody(event.target.value)} rows={3} autoFocus placeholder="Add a comment…" className="mt-4 w-full resize-none border border-rule bg-paperDeep px-3 py-2 font-display text-lg leading-snug text-charcoal placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-453" data-mp="7sjbp69jpnws" />
          <div className="mt-3 flex flex-wrap items-center gap-4" data-id="element-454" data-mp="1vsepg18hrbn3">
            <button type="button" onClick={submit} disabled={!body.trim() || sending} className="label border border-ink px-4 py-2 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper disabled:border-rule disabled:text-mute disabled:hover:bg-transparent disabled:hover:text-mute" data-id="element-455" data-mp="1guckbmltai3q">
              {sending ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => setComposing(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-456" data-mp="1zrxcb2jvhwsk">
              Cancel
            </button>
            {failed && <p role="alert" className="label-sm m-0 text-ink" data-id="element-457" data-mp="2x8w1ec2hgs3y">
                {failed}
              </p>}
          </div>
        </div>}

      {/* Margin markers, one per commented paragraph. */}
      {markers.map(marker => <button key={marker.paragraph} type="button" style={{
      top: marker.top
    }} onClick={() => setOpenMarker(openMarker === marker.paragraph ? null : marker.paragraph)} aria-label={`${marker.comments.length} comment${marker.comments.length === 1 ? '' : 's'} on this passage`} className="absolute right-0 flex items-center gap-1 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-458" data-mp="14jmyb52dcn9u">
          <MessageSquareIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-459" data-mp="1ij4rrxqm0opj" />
          {marker.comments.length > 1 && <span className="label-sm" data-id="element-460" data-mp="1djjsfm7j2eca">{marker.comments.length}</span>}
        </button>)}

      {/* The comments on one passage. */}
      {open && <div style={{
      top: open.top
    }} className="absolute right-8 z-30 w-[22rem] max-w-[90vw] border border-ink bg-paper p-5 shadow-sm" data-id="element-461" data-mp="269i8wt7np35c">
          <div className="flex items-baseline justify-between gap-4" data-id="element-462" data-mp="2ijzsvlt8at95">
            <span className="label text-graphite" data-id="element-463" data-mp="2imcjsogysdyc">Comments</span>
            <button type="button" onClick={() => setOpenMarker(null)} className="label-sm text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-464" data-mp="2niuip7pm5i2r">
              Close
            </button>
          </div>
          <ul className="m-0 mt-3 list-none p-0" data-id="element-465" data-mp="31ivjkn5vopi8">
            {open.comments.map(comment => <li key={comment.id} className="border-t border-rule py-4" data-id="element-466" data-mp="369ltmdp91gj9">
                <p className="m-0 font-display text-lg leading-snug text-charcoal" data-id="element-467" data-mp="3b971jdmecz84">{comment.body}</p>
                <span className="label-sm mt-2 block text-mute" data-id="element-468" data-mp="3g89ysbw4y15j">
                  {comment.authorName} · {formatEditStamp(comment.at)}
                </span>
              </li>)}
          </ul>
        </div>}
    </div>;
}